import configLoader from '@/runtime-config-loader';

export type SessionExpiryReason = 'expired' | 'unauthorized';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const ACTIVE_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
const REFRESH_RETRY_INTERVAL_MS = 60 * 1000;
const SESSION_CHECK_INTERVAL_MS = 10 * 1000;
const TIMER_PULSE_MS = 1000;
const SHARED_ACTIVITY_KEY = 'raws:lastAuthenticatedActivityAt';
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
    'click',
    'mousedown',
    'mousemove',
    'pointerdown',
    'keydown',
    'touchstart',
    'scroll',
];

class SessionGuard {
    private runId = 0;
    private expired = false;
    private pendingExpiryReason: SessionExpiryReason | null = null;
    private lastActivityAt = Date.now();
    private lastLocalActivityAt = Date.now();
    private idleDeadlineAt = Date.now() + IDLE_TIMEOUT_MS;
    private idleTimer: number | null = null;
    private refreshTokenExpiresAt: number | null = null;
    private timerWorker: Worker | null = null;
    private timerWorkerUrl: string | null = null;
    private fallbackTimer: number | null = null;
    private refreshInFlight = false;
    private checkInFlight = false;
    private lastRefreshAttemptAt = 0;
    private lastActiveRefreshAt = 0;
    private lastSessionCheckAt = 0;
    private lastMouseActivityAt = 0;
    private isProtectedOperation: () => boolean = () => false;
    private refreshSession: () => Promise<number | null> = async () => null;
    private checkSession: () => Promise<boolean> = async () => true;
    private listeners: Array<(reason: SessionExpiryReason) => void> = [];

    public start(
        isProtectedOperation: () => boolean,
        refreshSession: () => Promise<number | null>,
        checkSession: () => Promise<boolean> = async () => true,
    ) {
        const now = Date.now();
        this.stop();
        this.expired = false;
        this.pendingExpiryReason = null;
        this.lastActivityAt = now;
        this.lastLocalActivityAt = now;
        this.idleDeadlineAt = now + IDLE_TIMEOUT_MS;
        this.lastMouseActivityAt = 0;
        this.lastRefreshAttemptAt = 0;
        this.lastActiveRefreshAt = this.refreshTokenExpiresAt && this.refreshTokenExpiresAt > now ? now : 0;
        this.lastSessionCheckAt = 0;
        this.refreshInFlight = false;
        this.checkInFlight = false;
        this.isProtectedOperation = isProtectedOperation;
        this.refreshSession = refreshSession;
        this.checkSession = checkSession;
        this.shareActivity(now);

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, this.registerActivity, { capture: true, passive: true });
        });
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('focus', this.handleFocus);

        this.startTimer();
        this.scheduleIdleExpiration();
        this.tick();
    }

    public stop() {
        this.runId += 1;
        this.stopTimer();
        if (this.idleTimer !== null) {
            window.clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
        ACTIVITY_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, this.registerActivity, true);
        });
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('focus', this.handleFocus);
    }

    public subscribe(listener: (reason: SessionExpiryReason) => void): () => void {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter((candidate) => candidate !== listener);
        };
    }

    public touch() {
        if (!this.expired) {
            this.lastActivityAt = Date.now();
            this.lastLocalActivityAt = this.lastActivityAt;
            this.idleDeadlineAt = this.lastActivityAt + IDLE_TIMEOUT_MS;
            this.shareActivity(this.lastActivityAt);
            this.scheduleIdleExpiration();
        }
    }

    public getRemainingSeconds(): number {
        if (this.expired) return 0;
        if (this.isProtectedOperation()) return IDLE_TIMEOUT_MS / 1000;
        this.syncSharedActivity();
        return Math.max(0, Math.ceil((this.idleDeadlineAt - Date.now()) / 1000));
    }

    private shareActivity(at: number) {
        try {
            localStorage.setItem(SHARED_ACTIVITY_KEY, String(at));
        } catch (_) {
            // Session handling still works within this tab if browser storage is unavailable.
        }
    }

    private syncSharedActivity() {
        try {
            const sharedAt = Number(localStorage.getItem(SHARED_ACTIVITY_KEY));
            if (sharedAt > this.lastActivityAt && sharedAt <= Date.now()) {
                this.lastActivityAt = sharedAt;
                this.idleDeadlineAt = sharedAt + IDLE_TIMEOUT_MS;
            }
        } catch (_) {
            // Session handling still works within this tab if browser storage is unavailable.
        }
    }

    public registerRefreshTokenExpiry(expiresAt: number | null | undefined) {
        if (!expiresAt) {
            return;
        }
        this.refreshTokenExpiresAt = expiresAt * 1000;
        this.lastActiveRefreshAt = Date.now();
    }

    public expire(reason: SessionExpiryReason): boolean {
        if (this.expired) {
            return false;
        }
        if (this.isProtectedOperation()) {
            this.pendingExpiryReason = reason;
            return false;
        }
        this.expired = true;
        this.pendingExpiryReason = null;
        this.stop();
        this.listeners.forEach((listener) => listener(reason));
        return true;
    }

    private registerActivity = (event: Event) => {
        const now = Date.now();
        if (event.type === 'mousemove') {
            if (now - this.lastMouseActivityAt < 1000) {
                return;
            }
            this.lastMouseActivityAt = now;
        }
        const shouldForceSessionCheck = now - this.lastActivityAt >= SESSION_CHECK_INTERVAL_MS;
        this.touch();
        this.tick(shouldForceSessionCheck);
    }

    private handleVisibilityChange = () => {
        if (!document.hidden) {
            this.tick(true);
        }
    }

    private handleFocus = () => {
        this.tick(true);
    }

    private startTimer() {
        this.stopTimer();
        try {
            const workerSource = `
                const pulse = ${TIMER_PULSE_MS};
                setInterval(() => postMessage('tick'), pulse);
            `;
            this.timerWorkerUrl = URL.createObjectURL(
                new Blob([workerSource], { type: 'application/javascript' }),
            );
            this.timerWorker = new Worker(this.timerWorkerUrl);
            this.timerWorker.onmessage = () => this.tick();
            this.timerWorker.onerror = () => {
                this.stopWorker();
                this.startFallbackTimer();
            };
        } catch (_) {
            this.startFallbackTimer();
        }
    }

    private stopTimer() {
        this.stopWorker();
        if (this.fallbackTimer !== null) {
            window.clearInterval(this.fallbackTimer);
            this.fallbackTimer = null;
        }
    }

    private stopWorker() {
        if (this.timerWorker) {
            this.timerWorker.terminate();
            this.timerWorker = null;
        }
        if (this.timerWorkerUrl) {
            URL.revokeObjectURL(this.timerWorkerUrl);
            this.timerWorkerUrl = null;
        }
    }

    private startFallbackTimer() {
        if (this.fallbackTimer === null) {
            this.fallbackTimer = window.setInterval(() => this.tick(), TIMER_PULSE_MS);
        }
    }

    private scheduleIdleExpiration() {
        if (this.idleTimer !== null) {
            window.clearTimeout(this.idleTimer);
        }
        this.idleTimer = window.setTimeout(() => this.expireFromIdle(), Math.max(0, this.idleDeadlineAt - Date.now()));
    }

    private async expireFromIdle() {
        if (this.expired) {
            return;
        }
        this.syncSharedActivity();
        if (this.isProtectedOperation()) {
            this.touch();
            return;
        }
        if (Date.now() < this.idleDeadlineAt) {
            this.scheduleIdleExpiration();
            return;
        }
        this.expire('expired');
        try {
            const config = await configLoader;
            await fetch(`${config.backendUrl}/user/logout`, { method: 'POST', credentials: 'include' });
        } catch (_) {
            // The logout screen remains available when the connection is unavailable.
        }
    }

    private async tick(forceSessionCheck = false) {
        if (this.expired) {
            return;
        }
        if (this.pendingExpiryReason) {
            this.expire(this.pendingExpiryReason);
            return;
        }

        const runId = this.runId;
        const now = Date.now();
        this.syncSharedActivity();
        const protectedOperation = this.isProtectedOperation();
        const recentlyActive = protectedOperation || now - this.lastActivityAt <= IDLE_TIMEOUT_MS;
        if (protectedOperation) {
            this.touch();
        } else if (now >= this.idleDeadlineAt) {
            await this.expireFromIdle();
            return;
        }
        const shouldRefresh =
            (protectedOperation || now - this.lastLocalActivityAt <= SESSION_CHECK_INTERVAL_MS) && (
                !this.refreshTokenExpiresAt ||
                this.refreshTokenExpiresAt - now <= REFRESH_BEFORE_EXPIRY_MS ||
                now - this.lastActiveRefreshAt >= ACTIVE_REFRESH_INTERVAL_MS
            );

        await this.refreshIfNeeded(shouldRefresh, runId);
        if (this.expired || runId !== this.runId) {
            return;
        }

        await this.checkSessionIfNeeded(
            (forceSessionCheck || recentlyActive) && !document.hidden,
            forceSessionCheck,
            runId,
        );
    }

    private async checkSessionIfNeeded(shouldCheck: boolean, force: boolean, runId: number) {
        const now = Date.now();
        if (runId !== this.runId || !shouldCheck || this.checkInFlight || (!force && now - this.lastSessionCheckAt < SESSION_CHECK_INTERVAL_MS)) {
            return;
        }

        this.checkInFlight = true;
        this.lastSessionCheckAt = now;
        try {
            if (!(await this.checkSession()) && runId === this.runId) {
                this.expire('unauthorized');
            }
        } catch (_) {
            // A temporary connectivity problem is not proof that the session expired.
        } finally {
            if (runId === this.runId) {
                this.checkInFlight = false;
            }
        }
    }

    private async refreshIfNeeded(shouldExtend: boolean, runId: number) {
        const now = Date.now();
        if (runId !== this.runId || !shouldExtend || this.refreshInFlight || now - this.lastRefreshAttemptAt < REFRESH_RETRY_INTERVAL_MS) {
            return;
        }

        this.refreshInFlight = true;
        this.lastRefreshAttemptAt = now;
        try {
            const expiresAt = await this.refreshSession();
            if (runId !== this.runId) {
                return;
            }
            if (expiresAt) {
                this.registerRefreshTokenExpiry(expiresAt);
                this.lastActiveRefreshAt = Date.now();
            } else {
                this.expire('unauthorized');
            }
        } catch (_) {
            // A temporary connectivity problem is not proof that the refresh token expired.
        } finally {
            if (runId === this.runId) {
                this.refreshInFlight = false;
            }
        }
    }
}

export default new SessionGuard();
